import React from "react";
import PropTypes from "prop-types";
import { Typography, useTheme, Box, Skeleton } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Item } from "./Item";
import DashboardCardHeader from "./DashboardCardHeader";

const CardDashboardItemHorizontalMulti = ({
  titles,
  contents,
  isLoading = false,
  tooltipTitles,
  tooltipContents,
}) => {
  const theme = useTheme();

  if (
    !isLoading &&
    (titles.length !== contents.length ||
      titles.length < 2 ||
      titles.length > 6)
  ) {
    console.error(
      "CardDashboardItemHorizontalMulti requires between 2 and 6 items with matching titles and contents.",
    );
    return null;
  }

  // Calculate the number of items to display (default to 3 for loading state)
  const itemCount = isLoading ? 3 : titles.length;

  // Calculate grid size based on number of items
  const gridSize = 12 / itemCount;
  const gridSizeXs = itemCount === 4 ? 12 / 2 : gridSize; // For 4 items, use 6 columns on xs screens

  return (
    <Item
      sx={{
        p: 2.5,
        margin: 0,
        bgcolor: theme.palette.background.default,
        border: `1px solid ${theme.palette.primary.border2}`,
        borderRadius: "12px",
        boxShadow: theme.shadows[2],
      }}
    >
      <Grid container spacing={2}>
        {isLoading
          ? // Skeleton loading state
            Array(3)
              .fill(0)
              .map((_, index) => (
                <Grid key={index} size={{ xs: gridSizeXs, sm: gridSize }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      mb: "0.5rem",
                    }}
                  >
                    <Skeleton
                      variant="text"
                      width="70%"
                      height={32}
                      animation="wave"
                      sx={{ backgroundColor: theme.palette.background.default }}
                    />
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <Skeleton
                      variant="text"
                      width="50%"
                      height={40}
                      animation="wave"
                      sx={{ backgroundColor: theme.palette.background.default }}
                    />
                  </Box>
                </Grid>
              ))
          : // Actual content

            titles.map((title, index) => (
              <Grid key={index} size={{ xs: gridSizeXs, sm: gridSize || 12 }}>
                <Box sx={{ display: "flex", justifyContent: "center", mb: "0.5rem" }}>
                  <DashboardCardHeader
                    title={title}
                    tooltipTitle={tooltipTitles?.[index]}
                    tooltipContent={tooltipContents?.[index]}
                    titleVariant="body1"
                    titleColor={theme.palette.text.secondary}
                    titleSx={{ textAlign: "center", fontWeight: 600 }}
                  />
                </Box>
                <Box>
                  <Typography
                    variant="h4"
                    align="center"
                    sx={{
                      fontWeight: 700,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {contents[index]}
                  </Typography>
                </Box>
              </Grid>
            ))}
      </Grid>
    </Item>
  );
};

CardDashboardItemHorizontalMulti.propTypes = {
  titles: PropTypes.arrayOf(PropTypes.string).isRequired,
  contents: PropTypes.arrayOf(PropTypes.node).isRequired,
  isLoading: PropTypes.bool,
  tooltipTitles: PropTypes.arrayOf(PropTypes.string),
  tooltipContents: PropTypes.arrayOf(PropTypes.string),
};

export default CardDashboardItemHorizontalMulti;
